import React, { useRef, useLayoutEffect } from 'react';
import type { CampaignCard as CampaignCardStyles } from '../css/CampaignCard.modules';

interface CampaignCardBackgroundProps {
  backgroundImageUrl: string;
  hasMany: boolean;
  isCollapsed: boolean;
  campaignCardStyles: CampaignCardStyles;
  hasManyStyles: CampaignCardStyles;
}

const CampaignCardBackground: React.FC<CampaignCardBackgroundProps> = ({
  backgroundImageUrl,
  hasMany,
  isCollapsed,
  campaignCardStyles,
  hasManyStyles
}) => {
  const outgoingRef = useRef<HTMLDivElement>(null);
  const prevUrlRef = useRef(backgroundImageUrl);

  useLayoutEffect(() => {
    if (prevUrlRef.current === backgroundImageUrl) return;

    const el = outgoingRef.current;

    // Only crossfade for single campaign when collapsing.
    // hasMany uses CSS opacity + dark background-color masking instead.
    if (el && isCollapsed && !hasMany) {
      el.style.backgroundImage = `url(${prevUrlRef.current})`;
      el.setAttribute('data-fading', 'true');

      const handleEnd = () => {
        el.removeAttribute('data-fading');
        el.style.backgroundImage = '';
      };
      el.addEventListener('animationend', handleEnd, { once: true });
    }

    prevUrlRef.current = backgroundImageUrl;
  }, [backgroundImageUrl, isCollapsed, hasMany]);

  const className = `${campaignCardStyles.backgroundImage} ${hasMany ? hasManyStyles.backgroundImage : ''}`;

  return (
    <>
      {/* Current layer — always shows the latest image */}
      <div
        style={{ backgroundImage: `url(${backgroundImageUrl})` }}
        className={className}
        data-has-many={hasMany}
        data-collapsed={isCollapsed}
      />
      {/* Outgoing layer — single campaign crossfade only */}
      {!hasMany && (
        <div
          ref={outgoingRef}
          className={`${className} ${campaignCardStyles.backgroundImageOutgoing}`}
          data-has-many={hasMany}
        />
      )}
    </>
  );
};

export default CampaignCardBackground;
